// Play / Game state, top-left, the height of the controls panel: in Puzzle a
// constellation icon and the progress fraction with a caret; in Apogee the
// planets aligned so far and the score (docs/02-Game-Design/Alignments.md):
// none at first, then each planet in the order its alignment was first
// chosen, lit while in force and dimmed while it is free again; just the
// score until the first. The order is the player's own. Each is a button with a
// caret that opens its detail: the goal, or the words' scores. In
// Assemblage a star icon and the stars still unused, with no detail to open
// (docs/02-Game-Design/Assemblage Mode.md).
import { scorePlay, starsTotal, starsUsed, type PlayLevel, type PlayState } from '@wits/core';
import type { PlayRecord } from '../../systems/api/types';
import { Icon } from '../components/Icon';
import { PlanetIcon } from '../sky/PlanetIcon';

interface Props {
  level: PlayLevel;
  board: PlayState;
  play: PlayRecord;
  detailOpen: boolean;
  onToggleDetail: () => void;
}

export function GameStateBar({ level, board, play, detailOpen, onToggleDetail }: Props) {
  if (level.mode === 'puzzle') {
    const total = play.puzzle?.blanks.length ?? 0;
    const found = play.puzzle?.blanks.filter((b) => b.word !== null).length ?? 0;
    return (
      <button type="button" className="state-bar panel is-button" onClick={onToggleDetail} aria-expanded={detailOpen}>
        <Icon name="constellation" className="state-icon" />
        <span className="state-number mono">
          {found} / {total}
        </span>
        <Icon name={detailOpen ? 'caret-up' : 'caret-down'} small className="quiet" />
      </button>
    );
  }
  if (level.mode === 'assemblage') {
    // From the board, so a removal shows at once; a blocked star is still unused.
    const remaining = starsTotal(level) - starsUsed(board);
    return (
      <div className="state-bar panel" role="status" aria-label={`${remaining} ${remaining === 1 ? 'star' : 'stars'} remaining`}>
        <Icon name="star" className="state-icon" />
        <span className="state-number mono">{remaining}</span>
      </div>
    );
  }
  const scored = scorePlay(level, board);
  const score = play.status === 'finished' && play.score?.final !== null && play.score?.final !== undefined ? play.score.final : scored.running;
  // In the order chosen, which is the order each planet was first surrounded.
  const aligned = scored.alignments;
  return (
    <button type="button" className="state-bar panel is-button" onClick={onToggleDetail} aria-expanded={detailOpen}>
      {aligned.length > 0 && (
        <span className="planet-chips" aria-label="The planets aligned so far">
          {aligned.map((a) => (
            <PlanetIcon key={a.planet.id} level={level} planet={a.planet} size={22} state={a.active ? 'active' : 'inactive'} />
          ))}
        </span>
      )}
      <span className="state-number mono">{score}</span>
      <Icon name={detailOpen ? 'caret-up' : 'caret-down'} small className="quiet" />
    </button>
  );
}
